/**
 * Human-readable summaries for workflow chat messages.
 *
 * The Workflow chat used to dump raw JSON for every tool call and most AI step
 * outputs. This module turns each one into a single readable line; the renderer
 * keeps the original JSON behind a "View details" toggle so nothing is lost.
 *
 * Mapping derived from the real message shapes in `bytebridge_sandbox`
 * (17 tools, 8 steps). See Docs/mockups/chat_summary_mapping.md for the full
 * table and Docs/mockups/chat_summary_mockup.html for the visual reference.
 *
 * Adding a new tool/step: add a case below. Anything unmapped falls back to a
 * generic line plus the raw JSON, so an unknown step is never *worse* than the
 * old behaviour.
 */

// NOTHING is hidden by this change — every message that was visible before is still
// visible, just rendered as a readable line instead of a JSON dump.
// `get_all_netsuite_items` is the single exception, and it was ALREADY hidden before
// this change (it returns the entire item catalog). Left as it was.
export const HIDDEN_TOOLS = new Set([
  'get_all_netsuite_items',
]);

// Each tool emits a `tool_call` then a `tool_result`. Both are shown: the call as an
// in-progress line ("Looking up PO01005660…") and the result as an outcome line.
// Set to false to collapse each action to a single result-only line.
export const SHOW_TOOL_CALLS = true;

/* ------------------------------------------------------------------ helpers */

/** Pull the first {...} blob out of a tool message body and parse it. */
export function parseJsonBlob(text) {
  if (typeof text !== 'string') return null;
  // Stored content escapes newlines as literal "\n" — normalise first.
  const s = text.replace(/\\n/g, '\n');
  const a = s.indexOf('{');
  const b = s.lastIndexOf('}');
  if (a === -1 || b <= a) return null;
  try {
    return JSON.parse(s.slice(a, b + 1));
  } catch (e) {
    return null;
  }
}

/** "Executing: x" / "Completed: x" → "x". Falls back to message.tool_name. */
export function toolNameFrom(content, fallback) {
  if (typeof content === 'string') {
    const m = content.replace(/\\n/g, '\n').match(/^\s*(?:Executing|Completed):\s*(\S+)/m);
    if (m) return m[1];
  }
  return fallback || null;
}

const n = (v) => {
  const f = parseFloat(v);
  return Number.isFinite(f) ? f : 0;
};

const money = (v) => {
  const f = parseFloat(v);
  if (!Number.isFinite(f)) return null;
  return '$' + f.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const plural = (c, word) => `${c} ${word}${c === 1 ? '' : 's'}`;

const arr = (v) => (Array.isArray(v) ? v : []);

/** The error step freeforms its output — 10 different key shapes observed. */
export function pickErrorMessage(o) {
  if (!o || typeof o !== 'object') return null;
  const direct = o.error_message || o.message || o.reason || o.error_description;
  if (typeof direct === 'string' && direct.trim()) return direct.trim();
  if (typeof o.error === 'string' && o.error.trim()) return o.error.trim();
  if (o.error && typeof o.error === 'object') {
    const nested = o.error.message || o.error.error_message;
    if (typeof nested === 'string' && nested.trim()) return nested.trim();
  }
  if (typeof o.error_analysis === 'string' && o.error_analysis.trim()) return o.error_analysis.trim();
  return null;
}

/** Action links that must stay visible (not hidden behind the toggle). */
export function actionLinks(o) {
  if (!o || typeof o !== 'object') return {};
  return {
    netsuiteUrl: o.netsuite_url || o.bill_url || o.netsuite_bill_url || null,
    onedriveUrl: o.onedrive_url || null,
  };
}

/** Tools this module can summarise. Anything else keeps the original rendering. */
export const MAPPED_TOOLS = new Set([
  'identify_subsidiary_from_bill',
  'get_po_details_by_number',
  'search_netsuite_expense_account',
  'create_bill_from_purchase_order',
  'create_netsuite_vendor_bill',
  'search_netsuite_item_by_name',
  'lookup_memorized_item',
  'lookup_memorized_vendor',
  'get_netsuite_vendor_details',
  'validate_vendor_currency',
  // reference-data lookups — shown, not hidden
  'get_po_linked_bill_schema',
  'get_vendor_bill_schema',
  'get_netsuite_class',
  'get_netsuite_department',
  'get_netsuite_location',
  'get_netsuite_tax_codes',
]);

export const isMappedTool = (name) => !!name && MAPPED_TOOLS.has(name);

/**
 * In-progress line for a `tool_call`. Reads the PARAMETERS, never a response, so it
 * can never report success or failure — it only says what is being attempted.
 * Field names taken from the real call payloads in bytebridge_sandbox.
 */
function summarizeToolCall(toolName, o) {
  const q = (s) => `“${s}”`;

  switch (toolName) {
    case 'identify_subsidiary_from_bill': {
      const a = (o.bill_data && o.bill_data.bill_to_address) || {};
      const where = [a.company_name, a.city, a.country].filter(Boolean).join(', ');
      return {
        icon: '🏢',
        title: 'Identifying subsidiary from the billing address…',
        meta: where || undefined,
      };
    }

    case 'get_po_details_by_number':
      return {
        icon: '📋',
        title: `Looking up ${o.po_number || 'purchase order'} in NetSuite…`,
      };

    case 'search_netsuite_expense_account':
      return {
        icon: '💳',
        title: o.search_term
          ? `Searching expense accounts for ${q(o.search_term)}…`
          : 'Searching expense accounts…',
      };

    case 'create_bill_from_purchase_order': {
      const p = o.bill_payload || {};
      const items = arr(p.item && p.item.items).length;
      const exps = arr((p.expense && (p.expense.expenses || p.expense.items)) || []).length;
      const bits = [];
      if (items) bits.push(plural(items, 'item line'));
      if (exps) bits.push(plural(exps, 'expense line'));
      if (p.tranId) bits.push(`invoice ${p.tranId}`);
      return {
        icon: '🧾',
        title: 'Creating bill from purchase order in NetSuite…',
        meta: bits.length ? bits.join(' · ') : undefined,
      };
    }

    case 'create_netsuite_vendor_bill': {
      const p = o.bill_payload || {};
      return {
        icon: '🧾',
        title: 'Creating bill in NetSuite…',
        meta: p.tranId ? `invoice ${p.tranId}` : undefined,
      };
    }

    case 'search_netsuite_item_by_name':
      return {
        icon: '🔍',
        title: o.search_term ? `Searching items for ${q(o.search_term)}…` : 'Searching items…',
        meta: o.line_number ? `invoice line ${o.line_number}` : undefined,
      };

    case 'lookup_memorized_item':
      return {
        icon: '🧠',
        title: o.item_name_from_pdf
          ? `Checking memorized item mapping for ${q(o.item_name_from_pdf)}…`
          : 'Checking memorized item mapping…',
        meta: o.line_number ? `invoice line ${o.line_number}` : undefined,
      };

    case 'lookup_memorized_vendor':
      return {
        icon: '🧠',
        title: o.vendor_name_from_pdf
          ? `Checking memorized vendor mapping for ${q(o.vendor_name_from_pdf)}…`
          : 'Checking memorized vendor mapping…',
      };

    case 'get_netsuite_vendor_details':
      return {
        icon: '🏷️',
        title: o.vendor_name
          ? `Fetching vendor details for ${q(o.vendor_name)}…`
          : 'Fetching vendor details…',
      };

    case 'validate_vendor_currency':
      return {
        icon: '💱',
        title: o.pdf_currency_code
          ? `Checking the vendor accepts ${o.pdf_currency_code}…`
          : 'Checking vendor currency…',
      };

    case 'get_po_linked_bill_schema':
    case 'get_vendor_bill_schema':
      return { icon: '📐', title: 'Loading bill creation rules…' };

    case 'get_netsuite_class':
      return { icon: '🗂️', title: 'Loading classes from NetSuite…' };

    case 'get_netsuite_department':
      return { icon: '🗂️', title: 'Loading departments from NetSuite…' };

    case 'get_netsuite_location':
      return { icon: '🗂️', title: 'Loading locations from NetSuite…' };

    case 'get_netsuite_tax_codes':
      return {
        icon: '🧮',
        title: 'Loading tax codes from NetSuite…',
        meta: o.tax_label ? `invoice tax ${o.tax_label}` : undefined,
      };

    default:
      return null;
  }
}

/* -------------------------------------------------------------- tool results */

/**
 * A `tool_call` carries PARAMETERS, a `tool_result` carries a RESPONSE — completely
 * different shapes. `kind` keeps them apart: without it, a call payload would be read
 * as a result and every "Executing:" line would render as a false failure.
 *
 * @param {string} toolName
 * @param {object|null} json  parsed Parameters (call) or Response (result)
 * @param {'call'|'result'} kind
 * @returns {{icon:string,title:string,meta?:string,tone?:'ok'|'error'}|null}
 */
export function summarizeTool(toolName, json, kind) {
  if (!toolName) return null;
  const o = json || {};

  if (kind === 'call') return summarizeToolCall(toolName, o);

  switch (toolName) {
    case 'get_po_linked_bill_schema':
    case 'get_vendor_bill_schema':
      return { icon: '📐', title: 'Bill creation rules loaded' };

    case 'get_netsuite_class': {
      const c = arr(o.items).length;
      return { icon: '🗂️', title: `${c} ${c === 1 ? 'class' : 'classes'} loaded` };
    }

    case 'get_netsuite_department':
      return { icon: '🗂️', title: `${plural(arr(o.items).length, 'department')} loaded` };

    case 'get_netsuite_location':
      return { icon: '🗂️', title: `${plural(arr(o.items).length, 'location')} loaded` };

    case 'get_netsuite_tax_codes':
      return { icon: '🧮', title: `${plural(arr(o.tax_codes).length, 'tax code')} loaded` };

    case 'identify_subsidiary_from_bill': {
      if (o.subsidiary_name) {
        return {
          icon: '🏢',
          title: `Subsidiary identified: ${o.subsidiary_name}`,
          meta: o.subsidiary_id ? `NetSuite id ${o.subsidiary_id}` : undefined,
        };
      }
      return { icon: '🏢', title: 'Could not identify subsidiary from the bill', tone: 'error' };
    }

    case 'get_po_details_by_number': {
      const pos = arr(o.open_purchase_orders);
      if (!pos.length) {
        return { icon: '📋', title: 'Purchase order not found in NetSuite', tone: 'error' };
      }
      const po = pos[0];
      const lines = arr(po.line_items);
      const open = lines.filter((l) => n(l.open_for_billing_quantity) > 0).length;
      const bits = [];
      if (po.vendor_name) bits.push(po.vendor_name);
      if (lines.length) bits.push(`${plural(lines.length, 'line')}, ${open} open for billing`);
      return {
        icon: '📋',
        title: `${po.po_number || 'Purchase order'} found`,
        meta: bits.length ? bits.join(' · ') : undefined,
      };
    }

    case 'search_netsuite_expense_account': {
      const accts = arr(o.expense_accounts_found);
      if (!accts.length) return { icon: '💳', title: 'No matching expense account found' };
      const a = accts[0];
      const label = a.name || a.account_name || a.accountsearchdisplaynamecopy || 'account';
      return {
        icon: '💳',
        title: `Expense account matched: ${a.id ? a.id + ' · ' : ''}${label}`,
        meta: accts.length > 1 ? `${accts.length} candidates` : undefined,
      };
    }

    case 'create_bill_from_purchase_order':
    case 'create_netsuite_vendor_bill': {
      if (o.status === 'success' || o.netsuite_bill_id) {
        return {
          icon: '✅',
          title: `Bill created in NetSuite${o.netsuite_bill_id ? ' — id ' + o.netsuite_bill_id : ''}`,
          tone: 'ok',
        };
      }
      return {
        icon: '⚠️',
        title: `Bill creation failed${o.message ? ' — ' + o.message : ''}`,
        meta: o.details || undefined,
        tone: 'error',
      };
    }

    case 'search_netsuite_item_by_name': {
      const matches = arr(o.potential_matches);
      const term = o.search_term_used ? `"${o.search_term_used}"` : 'item';
      return {
        icon: '🔍',
        title: matches.length
          ? `Searched items for ${term} — ${plural(matches.length, 'match')}`
          : `Searched items for ${term} — no matches`,
        meta: o.line_number_searched ? `invoice line ${o.line_number_searched}` : undefined,
      };
    }

    case 'lookup_memorized_item':
    case 'lookup_memorized_vendor': {
      const kind = toolName === 'lookup_memorized_item' ? 'item' : 'vendor';
      if (o.found) {
        return { icon: '🧠', title: `Memorized ${kind} mapping found` };
      }
      return { icon: '🧠', title: o.message || `No memorized ${kind} mapping found` };
    }

    case 'get_netsuite_vendor_details': {
      const vs = arr(o.vendors_found);
      if (!vs.length) return { icon: '🏷️', title: 'Vendor not found in NetSuite', tone: 'error' };
      const v = vs[0];
      const bits = [];
      if (v.netsuite_entity_id) bits.push(v.netsuite_entity_id);
      if (v.netsuite_currency_symbol) bits.push(v.netsuite_currency_symbol);
      return {
        icon: '🏷️',
        title: 'Vendor details loaded',
        meta: bits.length ? bits.join(' · ') : undefined,
      };
    }

    case 'validate_vendor_currency': {
      const accepted = arr(o.accepted_currencies).map((c) => c.symbol).filter(Boolean);
      if (o.currency_valid) {
        return {
          icon: '💱',
          title: 'Vendor currency accepted',
          meta: accepted.length ? `accepts ${accepted.join(', ')}` : undefined,
        };
      }
      return {
        icon: '💱',
        title: 'Currency mismatch — vendor does not accept the invoice currency',
        meta: accepted.length ? `vendor accepts ${accepted.join(', ')}` : undefined,
        tone: 'error',
      };
    }

    default:
      return null; // unmapped → caller falls back to the original renderer
  }
}

/* ------------------------------------------------------------- step outputs */

export function summarizeStep(stepName, obj) {
  if (!stepName || !obj || typeof obj !== 'object') return null;
  const o = obj;

  switch (stepName) {
    case 'bill_triage_step': {
      const po = o.next_action_required === 'process_po_bill_step';
      return {
        icon: '🔀',
        title: po ? 'Routed as a PO-linked bill' : 'Routed as a standalone bill (no PO)',
        meta: o.reasoning || undefined,
      };
    }

    case 'po_item_mapping_step': {
      if (o.po_found === false) {
        return {
          icon: '📋',
          title: `Purchase order ${o.po_number || ''} not found in NetSuite`.replace(/\s+/g, ' ').trim(),
          meta: o.error_message || undefined,
          tone: 'error',
        };
      }
      const num = (o.po_data && o.po_data.po_number) || o.po_number;
      const lines = arr(o.po_data && o.po_data.line_items).length;
      return {
        icon: '📋',
        title: `${num || 'Purchase order'} matched`,
        meta: lines ? `${plural(lines, 'PO line')} mapped` : undefined,
      };
    }

    case 'po_mismatch_resolution_step': {
      // The step already produces a human sentence on some runs — prefer it.
      if (typeof o.user_message === 'string' && o.user_message.trim()) {
        return { icon: '🔗', title: o.user_message.trim() };
      }
      const s = o.summary || {};
      const total = n(s.total_pdf_items);
      const matched = n(s.matched_to_po);
      const extra = n(s.extra_items);
      const bits = [];
      if (total) bits.push(`Matched ${matched} of ${total} invoice lines to PO lines`);
      else bits.push(`Matched ${plural(matched, 'invoice line')} to PO lines`);
      if (extra) bits.push(`${plural(extra, 'charge')} routed to expenses`);
      return { icon: '🔗', title: bits.join(' · ') };
    }

    case 'identify_tax_code_step': {
      if (o.skip_reason) {
        return { icon: '🧮', title: 'Tax code step skipped', meta: o.skip_reason };
      }
      const t = o.tax_code_data || {};
      const name = t.tax_code_name || t.itemid || t.name;
      const rate = t.rate !== undefined && t.rate !== null ? `${n(t.rate)}%` : null;
      return {
        icon: '🧮',
        title: name ? `Tax code: ${name}${rate ? ' (' + rate + ')' : ''}` : 'Tax code identified',
      };
    }

    case 'fetch_bill_defaults_step': {
      const b = o.bill_data || {};
      const label = (x) => (x && (x.name || x.id)) || null;
      const bits = [];
      const loc = label(b.default_location);
      const cls = label(b.default_class);
      const dep = label(b.default_department);
      if (loc) bits.push(`location ${loc}`);
      if (cls) bits.push(`class ${cls}`);
      if (dep) bits.push(`dept ${dep}`);
      return {
        icon: '⚙️',
        title: 'Bill defaults applied',
        meta: bits.length ? bits.join(' · ') : undefined,
      };
    }

    case 'search_and_confirm_netsuite_vendor_ai_step': {
      const v = o.vendor_data || {};
      const name = v.vendor_name || v.netsuite_vendor_name || v.name;
      if (name) {
        return {
          icon: '🏷️',
          title: `Vendor matched: ${name}`,
          meta: v.netsuite_vendor_id ? `NetSuite id ${v.netsuite_vendor_id}` : undefined,
        };
      }
      const cands = arr(o.available_vendors).length;
      if (cands) {
        return {
          icon: '🏷️',
          title: `${plural(cands, 'possible vendor')} — needs your choice`,
        };
      }
      return { icon: '🏷️', title: 'Vendor search completed' };
    }

    case 'halt_workflow_bill_creation_error': {
      const msg = pickErrorMessage(o);
      return {
        icon: '⚠️',
        title: msg ? `Bill creation failed — ${msg}` : 'Bill creation failed',
        meta: o.suggested_action || undefined,
        tone: 'error',
      };
    }

    case 'halt_workflow_ai_error_step': {
      const msg = o.message_for_user || pickErrorMessage(o);
      return {
        icon: '⚠️',
        title: msg ? `Workflow stopped — ${msg}` : 'Workflow stopped by an error',
        tone: 'error',
      };
    }

    case 'output_not_a_bill_step': {
      return { icon: '📄', title: o.message || 'Document is not a bill' };
    }

    case 'document_type_detection_step': {
      const t = o.document_type;
      const label = t === 'normal_bill' ? 'vendor bill' : (t || 'document').replace(/_/g, ' ');
      return { icon: '📄', title: `Recognised as a ${label}` };
    }

    case 'extract_and_confirm_bill_data_ai_step': {
      // The captured PDF data — headline only. The full extraction (every line
      // item, addresses, tax) stays in the JSON behind "View details".
      const b = o.bill_data || {};
      const items = arr(b.line_items).length;
      const head = [];
      if (items) head.push(`Extracted ${plural(items, 'line item')}`);
      else head.push('Bill data extracted');
      const amt = money(b.total_amount);
      if (amt) head.push(amt);
      if (b.currency) head.push(b.currency);

      const meta = [];
      if (b.vendor_name) meta.push(b.vendor_name);
      if (b.invoice_number) meta.push(`invoice ${b.invoice_number}`);
      if (b.po_number) meta.push(`PO ${b.po_number}`);
      if (b.bill_date) meta.push(`dated ${b.bill_date}`);
      if (b.due_date) meta.push(`due ${b.due_date}`);

      return {
        icon: '🧾',
        title: head.join(' · '),
        meta: meta.length ? meta.join(' · ') : undefined,
      };
    }

    default:
      return null;
  }
}
